import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IGetGroupDetailInfoResult, IGroupInfo } from '../../../ts/models/IGroupInfo';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { Groups } from '../../../ts/resources/Groups';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace, setSpaceInfoItem } from '../../actions/spaces/EnterSpaceAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { ServiceLocator } from '../../ServiceLocator';
import { GroupSubspaceName, groupSubspaceNameToExplorableItemType, SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedGroupSpace } from './ConnectedGroupSpace';
import { GroupSpaceOwnProps } from './GroupSpace';

export interface GroupSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly subspaceName: GroupSubspaceName;
  readonly groupId: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (props: GroupSpaceOwnProps) => JSX.Element;
}

export interface GroupSpaceProviderStateProps {}

export interface GroupSpaceProviderDispatchProps {
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly contentCountsUpToDate: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
}

export interface LocationProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface GroupSpaceProviderState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly groupInfo: IGroupInfo;
  readonly groupDetailsInfo: IGetGroupDetailInfoResult;
}

const InitialState: GroupSpaceProviderState = {
  initialRender: true,
  renderChildren: false,
  hasError: false,
  groupInfo: undefined,
  groupDetailsInfo: undefined
};

export type GroupSpaceProviderProps = LocationProps & Omit<GroupSpaceProviderOwnProps, 'renderSpace'>;

export function GroupSpaceProvider(props: GroupSpaceProviderProps) {
  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateFilters: true,
    shouldUpdateContentTypes: false,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: false
  };

  const spaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'group',
    subspaceName: props.subspaceName
  };

  const spaceProps = {
    siteName: props.siteName,
    groupId: props.groupId,
    subspaceName: props.subspaceName,
    routeLocationSearch: props.routeLocationSearch,
    angularContext: props.angularContext,
    urls: props.urls,
    renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedGroupSpaceProviderContainer {...spaceProps} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}

interface GroupSpaceProviderPropsFromAngular {
  readonly groups: Groups;
}

export type GroupSpaceProviderNonAngularProps = GroupSpaceProviderOwnProps & GroupSpaceProviderStateProps & GroupSpaceProviderDispatchProps;

export type GroupSpaceProviderContainerProps = GroupSpaceProviderNonAngularProps & GroupSpaceProviderPropsFromAngular;

export class GroupSpaceProviderContainer extends React.PureComponent<GroupSpaceProviderContainerProps, GroupSpaceProviderState> {

  private unmounted: boolean = false;

  public constructor(props: GroupSpaceProviderContainerProps) {
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren && this.haveAllDataNeededToRender()) {
      const props: GroupSpaceOwnProps = {
        angularContext: this.props.angularContext,
        urls: this.props.urls,
        reloadSpace: this.reloadSpace,
      };
      return this.props.renderSpace(props);
    } else if (this.state.hasError) {
      return <RouterError errorType={'group'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentDidMount(){
    this.getData();
  }

  public componentDidUpdate(prevProps: GroupSpaceProviderContainerProps) {
    if (this.changedGroup(prevProps)) {
      this.updateState(InitialState /* initial state for new group */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace();
      this.setState({initialRender: false, renderChildren: true});
    } else if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private changeSubspace() {
    this.setSpaceInfoAndEnterSpace();
    this.props.contentCountsUpToDate();

    if (this.props.subspaceName === 'details') {
      return;
    }

    this.clearAndSetSortOrderFromURL();

    this.props.requestSpaceContents();

  }

  private setSpaceInfoAndEnterSpace() {
    if (this.props.subspaceName === 'details') {
      this.props.enterSpace (
        {
          item: this.state.groupDetailsInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    } else {
      this.props.enterSpace(
        {
          item: this.state.groupInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    }
  }

  private clearAndSetSortOrderFromURL() {
    const contentTypes = [groupSubspaceNameToExplorableItemType(this.props.subspaceName) as ExplorableItemType];
    const spaceInfo = {item: undefined, spaceType: 'group', subspaceName: this.props.subspaceName} as SpaceInfo;
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private changedGroup(prevProps: GroupSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.groupId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.groupId !== this.props.groupId);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.groupInfo);
  }

  private getData = async () => {
    try {
      let [group, groupDetailsInfo] = await Promise.all([this.props.groups.getById(this.props.groupId), this.props.groups.getGroupDetails(this.props.groupId)]);
      this.updateState({groupInfo: group, groupDetailsInfo: groupDetailsInfo});
      this.props.updateBreadcrumbs([getGroupsPlaceBreadcrumb(this.props.urls), {text: group.name}]);
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private updateState<K extends keyof GroupSpaceProviderState>(newState: Pick<GroupSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private reloadSpace = async () => {
    await this.getData();
    if (this.props.subspaceName === 'users') {
      this.props.setSpaceInfoItem(
        {
          item: this.state.groupInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    } else {
      this.props.setSpaceInfoItem(
        {
          item: this.state.groupDetailsInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    }
  }
}

export namespace GroupSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const groups = ServiceLocator.get<Groups>('Groups');
    return {
      groups,
    };
  }

  function selectPropsFromAngularServices(angularServices: GroupSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<GroupSpaceProviderNonAngularProps, GroupSpaceProviderPropsFromAngular, GroupSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const renderSpace = (props: GroupSpaceOwnProps): JSX.Element => <ConnectedGroupSpace {...props} />;

const GroupSpaceProviderAng: React.ComponentType<GroupSpaceProviderNonAngularProps> = GroupSpaceProviderAngularProvider.HOC(GroupSpaceProviderContainer);

const mapDispatchToProps: MapDispatchToProps<GroupSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setSpaceInfoItem,
  requestSpaceContents,
  enterSpace,
  contentCountsUpToDate,
  setOrderAction,
  updateBreadcrumbs,
}, dispatch);

export const ConnectedGroupSpaceProviderContainer: React.ComponentClass<GroupSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(GroupSpaceProviderAng);

export function getGroupsPlaceBreadcrumb(urls: SpacesSiteUrls): BreadcrumbStateInfo {
  const href = urls.groupsUrl();
  return {
    id: href,
    text: Messages.allGroups(),
    href: href,
    testId: 'groups-link'
  };
}
